import React, {
  FunctionComponent,
  useState,
  SyntheticEvent,
  useEffect,
  useContext,
} from 'react';
import withStyles from 'react-jss';
import domToImage from 'retina-dom-to-image';
import { parse } from 'json2csv';
import { saveAs } from 'file-saver';
import pdfMake from 'pdfmake/build/pdfmake';
import { Redirect } from 'react-router-dom';
import queryString from 'query-string';
import classnames from 'classnames';
import { pathOr } from 'ramda';

import FormSelect from 'components/FormSelect';
import { ClassProp } from 'helpers/types';
import { BatchReportContext } from 'scenes/Report/scenes/BatchReport';
import Loading from 'components/Loading';
import { CSVParams } from 'scenes/CSVExport/CSVExport.helper';
import { BatchConfigSegmentedData } from 'scenes/Report/scenes/BatchReport/BatchReport.config';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';

import styles from './DownloadWidget.styles';
import BulkQuery from './components/BulkQuery';
import { filterOptions, docDefinitions } from './DownloadWidget.helpers';
import { DocumentNode } from 'graphql';
import {
  MerchantTransactionTotals,
  MerchantTransactionTotalsBySegment,
} from 'scenes/Report/scenes/ReportDetail/scenes/PerformanceReports/PerformanceOverview/PerformanceOverview.helper';

enum FormatOptions {
  CSV = 'CSV',
  PDF = 'PDF',
  PNG = 'PNG',
  DOWNLOADS = 'DOWNLOADS',
}

export type Format = 'CSV' | 'PDF' | 'PNG' | 'DOWNLOADS';
export type BatchData =
  | MerchantTransactionTotals[]
  | MerchantTransactionTotalsBySegment[];

export type Field = { label: string; value: string | (() => unknown) | any };

type BulkConfig = {
  query: DocumentNode;
  variables: Record<string, unknown>;
  dataPath: string[];
  queryName?: string;
  responseMapping?: Function;
};

type Props = {
  classes: ClassProp<ReturnType<typeof styles>>;
  nodeRef: React.MutableRefObject<null>;

  imageName?: string;
  className?: string;
  fields?: Field[];
  data?: any;
  options?: Format[];
  header?: JSX.Element;
  bulkConfig?: BulkConfig;
  csvName?: string;
  batchKey?: string;
  csvParams?: CSVParams;
  batchData?: BatchData;
  segmentedBatchData?: BatchConfigSegmentedData;
};

const DownloadWidget: FunctionComponent<Props> = ({
  classes,
  data,
  fields,
  imageName,
  options: userOptions,
  header,
  bulkConfig,
  batchKey,
  csvName = 'dump',
  csvParams,
  nodeRef,
  className,
  batchData,
  segmentedBatchData,
}) => {
  const [format, setFormat] = useState<Format | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  const [createRedirect, setCreateRedirect] = useState<boolean>(false);
  const [csvListRedirect, setCSVListRedirect] = useState<boolean>(false);

  const batchListener = useContext(BatchReportContext) as Function;
  const { userType } = useUserMgmt();

  const options = filterOptions(userType, userOptions);

  useEffect(() => {
    if (format) {
      downloadHandler();
    }

    if (batchListener && nodeRef.current) {
      batchHandler();
    }
  }, [format, nodeRef]);

  const onChange = (_: SyntheticEvent<HTMLElement, Event>, { value }: any) => {
    setFormat(value);
  };

  const batchHandler = async () => {
    const imageURL = await domToImage.toPng(nodeRef.current);
    const imageBlob = await domToImage.toBlob(nodeRef.current);
    batchListener(
      imageURL,
      imageBlob,
      batchKey || imageName,
      null,
      batchData,
      segmentedBatchData,
    );
  };

  const CSVHandler = (bulkData?: Record<string, unknown>) => {
    const CSVData = parse(bulkData || data, { fields });
    const file = new Blob([CSVData], {
      type: 'text/csv',
    });

    saveAs(file, `${csvName}.csv`);
  };

  const PNGHandler = async () => {
    setLoading(true);
    const image = await domToImage.toPng(nodeRef.current);

    saveAs(image, `${imageName}.png`);
    setLoading(false);
  };

  const PDFHandler = async () => {
    setLoading(true);
    const image = await domToImage.toPng(nodeRef.current);
    const pdfDocGenerator = pdfMake.createPdf(docDefinitions(image));

    pdfDocGenerator.getDataUrl((dataUrl: string) => {
      saveAs(dataUrl, `${imageName}.pdf`);
      setLoading(false);
    });
  };

  const CSVInterpreter = () => {
    if (csvParams) {
      setCreateRedirect(true);
    } else if (data) {
      CSVHandler();
    } else {
      setBulkLoading(true);
    }
  };

  const downloadHandler = () => {
    switch (format) {
      case FormatOptions.CSV:
        CSVInterpreter();
        break;

      case FormatOptions.PNG:
        PNGHandler();
        break;

      case FormatOptions.PDF:
        PDFHandler();
        break;

      case FormatOptions.DOWNLOADS:
        setCSVListRedirect(true);
    }

    setFormat(null);
  };

  const bulkOnCompleted = (data: unknown) => {
    const { dataPath, responseMapping } = bulkConfig as BulkConfig;
    const results = pathOr([], dataPath, data);

    CSVHandler(responseMapping ? results.map(responseMapping) : data);
    setBulkLoading(false);
  };

  const bulkOnError = () => {
    setBulkLoading(false);
  };

  return (
    <div className={classnames(classes.container, className)}>
      <Loading loading={loading} />
      {csvListRedirect && <Redirect to="/csv-export/list" />}

      {bulkLoading && (
        <BulkQuery
          onCompleted={bulkOnCompleted}
          onError={bulkOnError}
          {...(bulkConfig as BulkConfig)}
        />
      )}

      {createRedirect && (
        <Redirect
          to={{
            pathname: '/csv-export/create',
            search: queryString.stringify(csvParams || {}),
          }}
        />
      )}

      <div className={classes.header}>{header}</div>

      <div>
        <span className={classes.exportTitle}>Export As </span>

        <FormSelect
          fluid
          selectOnBlur={false}
          name="format"
          placeholder="Format"
          options={options}
          onChange={onChange}
          value={format}
        />
      </div>
    </div>
  );
};

export default withStyles(styles)(DownloadWidget);
